import { Trans, useTranslation } from 'react-i18next';

import { Select } from '@actual-app/components/select';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import type { AccountEntity } from '@actual-app/core/types/models';

import { useUpdateAccountMutation } from '#accounts';
import {
  Modal,
  ModalCloseButton,
  ModalHeader,
  ModalTitle,
} from '#components/common/Modal';
import { useAccount } from '#hooks/useAccount';
import { useCurrencyOptions } from '#hooks/useCurrencyOptions';
import { useSyncedPref } from '#hooks/useSyncedPref';

type AccountCurrencyModalProps = {
  accountId: AccountEntity['id'];
};

export function AccountCurrencyModal({ accountId }: AccountCurrencyModalProps) {
  const { t } = useTranslation();
  const account = useAccount(accountId);
  const [defaultCurrencyCode] = useSyncedPref('defaultCurrencyCode');
  const currencyOptions = useCurrencyOptions();
  const updateAccount = useUpdateAccountMutation();

  const options: [string, string][] = [
    [
      '',
      defaultCurrencyCode
        ? t('Budget default ({{code}})', { code: defaultCurrencyCode })
        : t('Budget default'),
    ],
    ...currencyOptions.filter(([code]) => code !== ''),
  ];

  const onChange = (code: string) => {
    updateAccount.mutate({
      account: { id: accountId, currency: code || null },
    });
  };

  return (
    <Modal name="account-currency">
      {({ state }) => (
        <>
          <ModalHeader
            title={
              <ModalTitle title={t('Account currency')} shrinkOnOverflow />
            }
            rightContent={<ModalCloseButton onPress={() => state.close()} />}
          />
          <View style={{ gap: 10 }}>
            <Select
              value={account?.currency ?? ''}
              onChange={onChange}
              options={options}
              disabled={account == null || updateAccount.isPending}
              style={{ width: '100%' }}
            />
            <Text style={{ color: theme.pageTextSubdued, fontSize: 13 }}>
              <Trans>
                Transactions in this account are entered in this currency.
                Budget totals and reports stay in the budget currency.
              </Trans>
            </Text>
          </View>
        </>
      )}
    </Modal>
  );
}
